import { supabase } from './supabaseClient';

export type AnalyticsEventName =
  | 'song_explored'
  | 'reaction_selected'
  | 'collection_added'
  | 'collection_removed'
  | 'action_modal_opened'
  | 'weekly_card_opened'
  | 'topic_filter_selected'
  | 'heartbeat';

export interface AnalyticsEventInput {
  event_name: AnalyticsEventName;
  song_id?: string;
  weekly_card_id?: string;
  action_modal?: string;
  topic_tag?: string;
  reaction?: string;
  meta?: Record<string, any>;
}

interface ProfileCountryData {
  country_code: string | null;
  country_name: string | null;
}

let cachedCountryData: ProfileCountryData | null = null;
let lastCountryFetch: number = 0;
const COUNTRY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function getOrCreateSessionId(): string {
  const storageKey = 'cb_session_id';
  let sessionId = localStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
}

function getMetadata(): Record<string, any> {
  return {
    route: window.location.pathname,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  };
}

async function getCountryData(userId: string): Promise<ProfileCountryData> {
  // Return cached data if fresh
  const now = Date.now();
  if (cachedCountryData && (now - lastCountryFetch) < COUNTRY_CACHE_DURATION) {
    return cachedCountryData;
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('country_code, country as country_name')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) {
      cachedCountryData = { country_code: null, country_name: null };
    } else {
      cachedCountryData = {
        country_code: data.country_code || null,
        country_name: data.country_name || null,
      };
    }

    lastCountryFetch = now;
    return cachedCountryData;
  } catch (err) {
    return { country_code: null, country_name: null };
  }
}

export async function trackEvent(event: AnalyticsEventInput): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const sessionId = getOrCreateSessionId();

    const meta = {
      ...getMetadata(),
      ...(event.meta || {}),
    };

    // Get country data if user is logged in
    let countryData: ProfileCountryData = { country_code: null, country_name: null };
    if (user?.id) {
      countryData = await getCountryData(user.id);
    }

    const eventData = {
      user_id: user?.id || null,
      session_id: sessionId,
      event_name: event.event_name,
      song_id: event.song_id || null,
      weekly_card_id: event.weekly_card_id || null,
      action_modal: event.action_modal || null,
      topic_tag: event.topic_tag || null,
      reaction: event.reaction || null,
      country_code: countryData.country_code,
      country_name: countryData.country_name,
      meta,
    };

    let retryCount = 0;
    const maxRetries = 2;
    let lastError: any = null;

    while (retryCount <= maxRetries) {
      const { error } = await supabase
        .from('analytics_events')
        .insert(eventData);

      if (!error) {
        if (retryCount > 0) {
          console.log(`[Analytics] Event tracked successfully after ${retryCount} ${retryCount === 1 ? 'retry' : 'retries'}`);
        }
        break;
      }

      lastError = error;
      retryCount++;

      if (retryCount <= maxRetries) {
        const delay = retryCount * 1000;
        console.warn(`[Analytics] Failed to track event (attempt ${retryCount}/${maxRetries + 1}), retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    if (lastError) {
      console.error('[Analytics] Failed to track event after all retries:', lastError.message);
    }

    // Update last_seen_at on heartbeat events
    if (event.event_name === 'heartbeat' && user?.id) {
      await supabase.rpc('update_user_heartbeat', { p_user_id: user.id });
    }
  } catch (err) {
    console.error('[Analytics] Unexpected error:', err);
  }
}
